import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import {
    Map,
    Upload,
    Plus,
    Search,
    MoreVertical,
    Eye,
    Edit,
    Trash2,
    MapPin,
    Loader2,
    ArrowLeft,
    Shield,
    PlayCircle,
    List,
    PenTool,
    CheckSquare,
    Square,
    Table2,
} from 'lucide-react';
import L from 'leaflet';
import toast from 'react-hot-toast';
import PolygonValidator from '../components/polygons/PolygonValidator';
import PolygonDetailsModal from '../components/polygons/PolygonDetailsModal';
import PolygonEditModal from '../components/polygons/PolygonEditModal';
import AnalysisModal from '../components/polygons/AnalysisModal';
import PolygonMap from '../components/map/PolygonMap';
import SpatialOperationsPanel from '../components/map/SpatialOperationsPanel';
import DrawingToolbar from '../components/map/DrawingToolbar';
import DrawingMetadataForm from '../components/map/DrawingMetadataForm';
import BatchOperationsBar from '../components/polygons/BatchOperationsBar';
import BulkMetadataEditor from '../components/polygons/BulkMetadataEditor';
import ExportMenu from '../components/polygons/ExportMenu';
import OverlapAlert from '../components/polygons/OverlapAlert';
import { polygonsApi, getErrorMessage } from '../lib/api';
import { useDashboardStore } from '../lib/store';

const ACCEPTED_FORMATS = {
    'application/json': ['.geojson', '.json'],
    'application/zip': ['.zip'],
    'application/vnd.google-earth.kml+xml': ['.kml'],
    'application/vnd.google-earth.kmz': ['.kmz'],
    'application/gpx+xml': ['.gpx'],
    'application/geopackage+sqlite3': ['.gpkg'],
};

const ACCEPTED_EXTENSIONS_LABEL = '.geojson, .zip (Shapefile), .kml, .kmz, .gpx, .gpkg';

export default function PolygonsPage() {
    const { t } = useTranslation();
    const { viewMode, setViewMode } = useDashboardStore();

    // Core state
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null);
    const [polygons, setPolygons] = useState<any[]>([]);
    const [mapFeatures, setMapFeatures] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [uploadCommodity, setUploadCommodity] = useState('cocoa');

    // Modals
    const [viewPolygon, setViewPolygon] = useState<any | null>(null);
    const [editPolygon, setEditPolygon] = useState<any | null>(null);
    const [analyzePolygon, setAnalyzePolygon] = useState<any | null>(null);

    // Map interaction
    const [mapSelectedId, setMapSelectedId] = useState<string | null>(null);
    const [spatialPolygon, setSpatialPolygon] = useState<any | null>(null);
    const [bufferPreview, setBufferPreview] = useState<any>(null);
    const [simplifyPreview, setSimplifyPreview] = useState<any>(null);
    const [highlightedIds, setHighlightedIds] = useState<string[]>([]);
    const [leafletMap, setLeafletMap] = useState<L.Map | null>(null);
    const [drawingActive, setDrawingActive] = useState(false);
    const [drawnGeometry, setDrawnGeometry] = useState<GeoJSON.Geometry | null>(null);

    // Batch selection
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [batchMode, setBatchMode] = useState(false);

    useEffect(() => { fetchPolygons(); }, []);

    const fetchPolygons = async () => {
        try {
            setLoading(true);
            setError(null);
            const [listData, geoData] = await Promise.all([
                polygonsApi.list({ page: 1, page_size: 100 }),
                polygonsApi.getGeoJSON(),
            ]);
            setPolygons(listData.items || []);
            setMapFeatures(geoData.features || []);
        } catch (err: any) {
            console.error('Error loading polygons:', err);
            setError(getErrorMessage(err) || 'Error al cargar polígonos');
            toast.error('Error al cargar polígonos');
        } finally {
            setLoading(false);
        }
    };

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            const file = acceptedFiles[0];
            if (!file) return;
            setUploading(true);
            try {
                const result = await polygonsApi.uploadFile(file, uploadCommodity);
                const msg = `"${file.name}" (${result.source_format}): ${result.created} polígono(s) creado(s).`;
                toast.success(result.warnings?.length ? msg + ` ${result.warnings.length} advertencia(s).` : msg);
                setShowUploadModal(false);
                await fetchPolygons();
            } catch (error: any) {
                toast.error(getErrorMessage(error) || 'Error al subir el archivo');
            } finally {
                setUploading(false);
            }
        },
        [uploadCommodity]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ACCEPTED_FORMATS,
        maxFiles: 1,
    });

    const handleDelete = async (polygonId: string, polygonName: string) => {
        if (!confirm(`¿Estás seguro de eliminar el polígono "${polygonName}"?`)) return;
        try {
            await polygonsApi.delete(polygonId);
            toast.success('Polígono eliminado exitosamente');
            await fetchPolygons();
        } catch (error: any) {
            toast.error(getErrorMessage(error) || 'Error al eliminar polígono');
        }
    };

    const handleAnalysisSuccess = async (jobId: string) => {
        try {
            await fetchPolygons();
            toast.success(`Análisis ${jobId.substring(0, 8)}... iniciado.`);
        } catch {
            toast.error('Análisis iniciado pero hubo un error al actualizar la lista');
        }
    };

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedIds(
            selectedIds.size === filteredPolygons.length
                ? new Set()
                : new Set(filteredPolygons.map((p) => p.id))
        );
    };

    const filteredPolygons = polygons.filter((p) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return p.name?.toLowerCase().includes(q) || p.producer_name?.toLowerCase().includes(q);
    });

    // --- EUDR Validator sub-view ---
    if (selectedPolygonId) {
        const selectedPolygon = polygons.find((p) => p.id === selectedPolygonId);
        return (
            <div className="space-y-6 animate-fade-in">
                <button
                    onClick={() => setSelectedPolygonId(null)}
                    className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Volver a lista de polígonos</span>
                </button>
                {selectedPolygon && (
                    <div className="card p-6 mb-6">
                        <div className="flex items-center gap-3">
                            <MapPin className="w-6 h-6 text-primary-600" />
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {selectedPolygon.name}
                                </h2>
                                <p className="text-sm text-gray-500">
                                    {selectedPolygon.producer_name} • {selectedPolygon.area_ha} ha •{' '}
                                    {selectedPolygon.region}, {selectedPolygon.country}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                <PolygonValidator polygonId={selectedPolygonId} />
            </div>
        );
    }

    // --- Main view ---
    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t('polygons.title')}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400">
                        Gestión de polígonos con validación técnica EUDR
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {/* View mode toggle */}
                    <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                        <button
                            onClick={() => setViewMode('map')}
                            className={`p-2 rounded-md transition-colors ${
                                viewMode === 'map'
                                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Vista de mapa"
                        >
                            <Map className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-md transition-colors ${
                                viewMode === 'list'
                                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Vista de lista"
                        >
                            <List className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            className={`p-2 rounded-md transition-colors ${
                                viewMode === 'table'
                                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Edición masiva"
                        >
                            <Table2 className="w-4 h-4" />
                        </button>
                    </div>

                    <OverlapAlert polygonCount={polygons.length} />
                    <ExportMenu disabled={polygons.length === 0} />

                    {/* Draw on map */}
                    {viewMode === 'map' && (
                        <button
                            onClick={() => {
                                setSpatialPolygon(null);
                                setDrawnGeometry(null);
                                setDrawingActive(true);
                            }}
                            disabled={drawingActive}
                            className={`btn-secondary flex items-center gap-2 text-sm ${
                                drawingActive ? 'ring-2 ring-green-500 bg-green-50 text-green-700' : ''
                            }`}
                        >
                            <PenTool className="w-4 h-4" />
                            Dibujar
                        </button>
                    )}

                    {/* Batch mode toggle (hidden in table view) */}
                    {viewMode !== 'table' && (
                        <button
                            onClick={() => {
                                setBatchMode(!batchMode);
                                if (batchMode) setSelectedIds(new Set());
                            }}
                            className={`btn-secondary flex items-center gap-2 text-sm ${
                                batchMode ? 'ring-2 ring-primary-500 bg-primary-50 text-primary-700' : ''
                            }`}
                        >
                            <CheckSquare className="w-4 h-4" />
                            {batchMode ? 'Salir selección' : 'Selección'}
                        </button>
                    )}

                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="btn-primary flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        {t('polygons.addPolygon')}
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={`${t('common.search')}...`}
                        className="input pl-12"
                    />
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">Cargando polígonos...</p>
                    </div>
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="card p-6">
                    <div className="flex items-start gap-3">
                        <Trash2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Error al Cargar Polígonos
                            </h3>
                            <p className="text-red-600">{error}</p>
                            <button onClick={fetchPolygons} className="btn-secondary mt-4">
                                Reintentar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && polygons.length === 0 && (
                <div className="card p-12 text-center">
                    <Map className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        No hay polígonos registrados
                    </h3>
                    <p className="text-gray-500 mb-6">
                        Sube tu primer archivo para comenzar (GeoJSON, Shapefile, KML, KMZ, GPX)
                    </p>
                    <button onClick={() => setShowUploadModal(true)} className="btn-primary">
                        <Upload className="w-4 h-4 mr-2" />
                        Subir archivo
                    </button>
                </div>
            )}

            {/* Batch operations bar */}
            {batchMode && (
                <BatchOperationsBar
                    selectedIds={selectedIds}
                    totalCount={filteredPolygons.length}
                    onToggleSelectAll={toggleSelectAll}
                    onClearSelection={() => { setSelectedIds(new Set()); setBatchMode(false); }}
                    onRefresh={fetchPolygons}
                />
            )}

            {/* MAP VIEW */}
            {!loading && !error && polygons.length > 0 && viewMode === 'map' && (
                <div className="space-y-4">
                    <div className="flex gap-4">
                        <div className={`${spatialPolygon || drawnGeometry ? 'flex-1' : 'w-full'} relative transition-all`}>
                            <PolygonMap
                                features={mapFeatures}
                                overlaps={[]}
                                showOverlaps={false}
                                selectedPolygonId={mapSelectedId}
                                onPolygonClick={(id) => {
                                    if (drawingActive) return;
                                    const newId = id === mapSelectedId ? null : id;
                                    setMapSelectedId(newId);
                                    if (newId) {
                                        const poly = polygons.find((p) => p.id === newId);
                                        if (poly) setSpatialPolygon(poly);
                                    } else {
                                        setSpatialPolygon(null);
                                        setBufferPreview(null);
                                        setSimplifyPreview(null);
                                        setHighlightedIds([]);
                                    }
                                }}
                                height="calc(100vh - 280px)"
                                bufferPreview={bufferPreview}
                                simplifyPreview={simplifyPreview}
                                highlightedPolygonIds={highlightedIds}
                                onMapReady={setLeafletMap}
                            />
                            <DrawingToolbar
                                map={leafletMap}
                                active={drawingActive}
                                onDrawComplete={(geom) => {
                                    setDrawnGeometry(geom);
                                    setDrawingActive(false);
                                }}
                                onDrawCancel={() => {
                                    setDrawingActive(false);
                                    setDrawnGeometry(null);
                                }}
                            />
                        </div>

                        {drawnGeometry && !spatialPolygon && (
                            <div className="w-96 flex-shrink-0">
                                <DrawingMetadataForm
                                    geometry={drawnGeometry}
                                    onSave={() => { setDrawnGeometry(null); fetchPolygons(); }}
                                    onCancel={() => setDrawnGeometry(null)}
                                />
                            </div>
                        )}
                        {spatialPolygon && (
                            <div className="w-96 flex-shrink-0">
                                <SpatialOperationsPanel
                                    polygon={spatialPolygon}
                                    onClose={() => {
                                        setSpatialPolygon(null);
                                        setMapSelectedId(null);
                                        setBufferPreview(null);
                                        setSimplifyPreview(null);
                                        setHighlightedIds([]);
                                    }}
                                    onBufferPreview={setBufferPreview}
                                    onSimplifyPreview={setSimplifyPreview}
                                    onNearbyHighlight={setHighlightedIds}
                                    onOverlapsFound={() => {}}
                                    onPolygonCreated={fetchPolygons}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* LIST VIEW */}
            {!loading && !error && polygons.length > 0 && viewMode === 'list' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPolygons.map((polygon) => (
                        <div
                            key={polygon.id}
                            className={`card-hover p-6 ${batchMode && selectedIds.has(polygon.id) ? 'ring-2 ring-primary-500 bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                            onClick={batchMode ? () => toggleSelect(polygon.id) : undefined}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    {batchMode ? (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); toggleSelect(polygon.id); }}
                                            className="w-12 h-12 rounded-xl flex items-center justify-center border-2 transition-colors"
                                            style={{
                                                borderColor: selectedIds.has(polygon.id) ? '#6366f1' : '#d1d5db',
                                                backgroundColor: selectedIds.has(polygon.id) ? '#eef2ff' : 'transparent',
                                            }}
                                        >
                                            {selectedIds.has(polygon.id) ? (
                                                <CheckSquare className="w-6 h-6 text-primary-600" />
                                            ) : (
                                                <Square className="w-6 h-6 text-gray-400" />
                                            )}
                                        </button>
                                    ) : (
                                        <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                                            <Map className="w-6 h-6 text-primary-600" />
                                        </div>
                                    )}
                                    <div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setViewMode('map'); setMapSelectedId(polygon.id); }}
                                            className="font-semibold text-primary-600 hover:text-primary-800 dark:text-primary-400 hover:underline text-left"
                                            title="Ver en el mapa"
                                        >
                                            {polygon.name}
                                        </button>
                                        <p className="text-sm text-gray-500">
                                            {polygon.producer_name}
                                        </p>
                                    </div>
                                </div>
                                <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                                    <MoreVertical className="w-5 h-5 text-gray-400" />
                                </button>
                            </div>

                            <div className="space-y-3 mb-4">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">{t('common.area')}</span>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        {polygon.area_ha} ha
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">{t('polygons.commodity')}</span>
                                    <span className="font-medium text-gray-900 dark:text-white capitalize">
                                        {t(`commodities.${polygon.commodity}`)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">{t('polygons.region')}</span>
                                    <span className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {polygon.region}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                                <span
                                    className={`${
                                        polygon.compliance_status === 'compliant'
                                            ? 'badge-success'
                                            : polygon.compliance_status === 'non_compliant'
                                              ? 'badge-danger'
                                              : 'badge-warning'
                                    }`}
                                >
                                    {polygon.compliance_status === 'compliant'
                                        ? t('compliance.compliant')
                                        : polygon.compliance_status === 'non_compliant'
                                          ? t('compliance.nonCompliant')
                                          : 'Pendiente'}
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setSelectedPolygonId(polygon.id)}
                                        className="p-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg group"
                                        title="Validación EUDR"
                                    >
                                        <Shield className="w-4 h-4 text-primary-600 group-hover:text-primary-700" />
                                    </button>
                                    <button
                                        onClick={() => setAnalyzePolygon(polygon)}
                                        className="p-2 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg group"
                                        title="Iniciar Análisis"
                                    >
                                        <PlayCircle className="w-4 h-4 text-green-600 group-hover:text-green-700" />
                                    </button>
                                    <button
                                        onClick={() => setViewPolygon(polygon)}
                                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                        title="Ver detalles"
                                    >
                                        <Eye className="w-4 h-4 text-gray-500" />
                                    </button>
                                    <button
                                        onClick={() => setEditPolygon(polygon)}
                                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                        title="Editar"
                                    >
                                        <Edit className="w-4 h-4 text-gray-500" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(polygon.id, polygon.name)}
                                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                                        title="Eliminar"
                                    >
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* TABLE VIEW (Bulk Editor) */}
            {!loading && !error && polygons.length > 0 && viewMode === 'table' && (
                <BulkMetadataEditor
                    polygons={filteredPolygons}
                    onSaveComplete={fetchPolygons}
                    onNavigateToPolygon={(id) => {
                        setViewMode('map');
                        setMapSelectedId(id);
                    }}
                />
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/50">
                    <div className="card p-8 w-full max-w-lg animate-slide-up">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            Subir archivo geoespacial
                        </h2>
                        <p className="text-gray-500 mb-4">
                            Soporta GeoJSON, Shapefile (ZIP), KML, KMZ, GPX, GeoPackage
                        </p>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Commodity
                            </label>
                            <select
                                value={uploadCommodity}
                                onChange={(e) => setUploadCommodity(e.target.value)}
                                className="input"
                            >
                                <option value="cocoa">Cacao</option>
                                <option value="coffee">Café</option>
                                <option value="palm_oil">Aceite de Palma</option>
                                <option value="soy">Soja</option>
                                <option value="timber">Madera</option>
                                <option value="rubber">Caucho</option>
                                <option value="cattle">Ganado</option>
                            </select>
                        </div>
                        <div
                            {...getRootProps()}
                            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors ${
                                isDragActive
                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                            }`}
                        >
                            <input {...getInputProps()} />
                            {uploading ? (
                                <>
                                    <Loader2 className="w-12 h-12 mx-auto text-primary-500 animate-spin mb-4" />
                                    <p className="text-gray-600 dark:text-gray-400">Procesando archivo...</p>
                                </>
                            ) : (
                                <>
                                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                                    <p className="text-gray-600 dark:text-gray-300">
                                        {isDragActive ? 'Suelta el archivo aquí...' : 'Arrastra un archivo o haz clic para seleccionar'}
                                    </p>
                                    <p className="text-sm text-gray-400 mt-2">{ACCEPTED_EXTENSIONS_LABEL}</p>
                                </>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setShowUploadModal(false)} className="btn-secondary" disabled={uploading}>
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {viewPolygon && (
                <PolygonDetailsModal polygon={viewPolygon} onClose={() => setViewPolygon(null)} />
            )}
            {editPolygon && (
                <PolygonEditModal polygon={editPolygon} onClose={() => setEditPolygon(null)} onSuccess={fetchPolygons} />
            )}
            {analyzePolygon && (
                <AnalysisModal polygon={analyzePolygon} onClose={() => setAnalyzePolygon(null)} onSuccess={handleAnalysisSuccess} />
            )}
        </div>
    );
}
